import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, UserPlus, PackagePlus } from 'lucide-react';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const businessId = localStorage.getItem('currentBusiness');
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({
    customerId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    type: 'GST_INVOICE',
    placeOfSupply: '',
    notes: '',
    terms: '',
    lines: []
  });
  const [loading, setLoading] = useState(false);

  // Quick add dialogs
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [newItem, setNewItem] = useState({ name: '', salePrice: 0, gstPercent: 18, hsn: '' });

  useEffect(() => {
    fetchCustomers();
    fetchItems();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get(`/api/customers/${businessId}`);
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await api.get(`/api/items/${businessId}`);
      setItems(response.data.items);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { itemId: '', name: '', qty: 1, rate: 0, gstPercent: 18, hsn: '', discount: 0 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    if (field === 'itemId') {
      const item = items.find(i => i.id === value);
      if (item) {
        newLines[index] = {
          ...newLines[index],
          name: item.name,
          rate: item.salePrice,
          gstPercent: item.gstPercent,
          hsn: item.hsn || ''
        };
      }
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const removeLine = (index) => {
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxAmount = 0;
    
    formData.lines.forEach(line => {
      const lineTotal = line.qty * line.rate - (line.discount || 0);
      subtotal += lineTotal;
      taxAmount += (lineTotal * line.gstPercent) / 100;
    });
    
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customerId) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.lines.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setLoading(true);
    try {
      const invoiceData = {
        businessId,
        customerId: formData.customerId,
        invoiceDate: new Date(formData.invoiceDate).toISOString(),
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
        type: formData.type,
        status: 'SENT',
        placeOfSupply: formData.placeOfSupply || null,
        lines: formData.lines,
        discount: formData.discount || 0,
        notes: formData.notes || null,
        terms: formData.terms || null,
        previousBalance: 0
      };
      
      console.log('Submitting invoice data:', invoiceData);
      const response = await api.post('/api/invoices', invoiceData);
      toast.success('Invoice created successfully');
      navigate(`/invoices/${response.data.invoice.id}`);
    } catch (error) {
      console.error('Invoice creation error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  const handleQuickAddCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/customers', { ...newCustomer, businessId });
      toast.success('Customer added successfully');
      setCustomers([...customers, response.data.customer]);
      setFormData({ ...formData, customerId: response.data.customer.id });
      setShowCustomerDialog(false);
      setNewCustomer({ name: '', phone: '', email: '' });
    } catch (error) {
      toast.error('Failed to add customer');
    }
  };

  const handleQuickAddItem = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/items', { ...newItem, businessId, stock: 0 });
      toast.success('Item added successfully');
      setItems([...items, response.data.item]);
      setShowItemDialog(false);
      setNewItem({ name: '', salePrice: 0, gstPercent: 18, hsn: '' });
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="create-invoice-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/invoices')} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading">Create Invoice</h1>
          <p className="text-sm text-gray-600 mt-1">Generate a new invoice for your customer</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerId">Customer *</Label>
                <div className="flex gap-2">
                  <Select value={formData.customerId} onValueChange={(value) => setFormData({ ...formData, customerId: value })}>
                    <SelectTrigger data-testid="customer-select" className="flex-1">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="outline" size="icon" data-testid="quick-add-customer-button">
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Quick Add Customer</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleQuickAddCustomer} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="quick-customer-name">Name *</Label>
                          <Input
                            id="quick-customer-name"
                            value={newCustomer.name}
                            onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="quick-customer-phone">Phone</Label>
                          <Input
                            id="quick-customer-phone"
                            value={newCustomer.phone}
                            onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="quick-customer-email">Email</Label>
                          <Input
                            id="quick-customer-email"
                            type="email"
                            value={newCustomer.email}
                            onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                          />
                        </div>
                        <Button type="submit" className="w-full">Add Customer</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Invoice Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger data-testid="invoice-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GST_INVOICE">GST Invoice</SelectItem>
                    <SelectItem value="BILL_OF_SUPPLY">Bill of Supply</SelectItem>
                    <SelectItem value="NON_GST">Non-GST Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoiceDate">Invoice Date *</Label>
                <Input
                  id="invoiceDate"
                  type="date"
                  value={formData.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                  required
                  data-testid="invoice-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  data-testid="due-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="placeOfSupply">Place of Supply</Label>
                <Input
                  id="placeOfSupply"
                  value={formData.placeOfSupply}
                  onChange={(e) => setFormData({ ...formData, placeOfSupply: e.target.value })}
                  placeholder="State name"
                  data-testid="place-of-supply-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Items</CardTitle>
            <div className="flex gap-2">
              <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" size="sm" data-testid="quick-add-item-button">
                    <PackagePlus className="w-4 h-4 mr-2" />
                    Quick Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Quick Add Item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleQuickAddItem} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="quick-item-name">Item Name *</Label>
                      <Input
                        id="quick-item-name"
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="quick-item-price">Sale Price *</Label>
                        <Input
                          id="quick-item-price"
                          type="number"
                          step="0.01"
                          value={newItem.salePrice}
                          onChange={(e) => setNewItem({ ...newItem, salePrice: parseFloat(e.target.value) })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quick-item-gst">GST %</Label>
                        <Input
                          id="quick-item-gst"
                          type="number"
                          step="0.01"
                          value={newItem.gstPercent}
                          onChange={(e) => setNewItem({ ...newItem, gstPercent: parseFloat(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quick-item-hsn">HSN Code</Label>
                      <Input
                        id="quick-item-hsn"
                        value={newItem.hsn}
                        onChange={(e) => setNewItem({ ...newItem, hsn: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full">Add Item</Button>
                  </form>
                </DialogContent>
              </Dialog>
              <Button type="button" onClick={addLine} variant="outline" size="sm" data-testid="add-item-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Line
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.lines.map((line, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3" data-testid="invoice-line-item">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                    <div className="lg:col-span-2">
                      <Label>Item</Label>
                      <Select value={line.itemId} onValueChange={(value) => updateLine(index, 'itemId', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select item" />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Qty</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={line.qty}
                        onChange={(e) => updateLine(index, 'qty', parseFloat(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label>Rate</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.rate}
                        onChange={(e) => updateLine(index, 'rate', parseFloat(e.target.value))}
                      />
                    </div>
                    <div>
                      <Label>GST %</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.gstPercent}
                        onChange={(e) => updateLine(index, 'gstPercent', parseFloat(e.target.value))}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => removeLine(index)}
                        data-testid="remove-item-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {formData.lines.length === 0 && (
                <p className="text-center text-gray-500 py-8">No items added. Click "Add Item" to begin.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end">
              <div className="w-full md:w-1/2 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">₹{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="font-semibold">₹{totals.taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="text-teal-700">₹{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Any special notes for this invoice"
                data-testid="invoice-notes-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="terms">Terms & Conditions</Label>
              <Textarea
                id="terms"
                value={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                rows={3}
                placeholder="Payment terms and conditions"
                data-testid="invoice-terms-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/invoices')} data-testid="cancel-button">
            Cancel
          </Button>
          <Button type="submit" disabled={loading} className="bg-teal-700 hover:bg-teal-800" data-testid="create-invoice-submit-button">
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;